import { debounceTime, Subscription } from 'rxjs';
import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';

import {
  IProductionField,
  IParametersCommission,
  IBankCommission,
  IProductCommission,
  ICompanyCommission,
  IProduct,
  IProductForm,
  IProductionForm,
  ICommission,
  IProductValues,
  IProduction
} from './../../../../models/commission';
import { ICustomSelect, ActionType } from 'src/app/models/generic';
import { ProductionFields } from 'src/app/shared/utils/comission.base';
import { ActionEnum } from './../../../../models/generic';

@Component({
  selector: 'app-comissao-form',
  templateUrl: './comissao-form.component.html',
  styleUrls: ['./comissao-form.component.scss']
})
export class ComissaoFormComponent implements OnInit, OnDestroy {
  @Input() parameters!: IParametersCommission;
  @Input() product!: IProduct;
  @Input() commission!: ICommission;
  @Input() type: ActionType = ActionEnum.Create;
  @Input() firstProduct: boolean = false;
  @Input() products: IProductCommission[] = [];

  @Output() changesProductIdValueEvent = new EventEmitter<string>();
  @Output() changesCommissionTypeValueEvent = new EventEmitter<number>();
  @Output() formValueEvent = new EventEmitter<IProduct>();
  @Output() changesFormValueEvent = new EventEmitter<IProductValues>();

  Action = ActionEnum;
  productionFields: IProductionField[] = ProductionFields;
  bankSelectFields: ICustomSelect[] = [];
  productSelectFields: ICustomSelect[] = [];
  companySelectFields: ICustomSelect[] = [];

  productForm!: FormGroup;
  transfer: boolean = false;

  changesProductValueSubscription!: Subscription;
  valueChangesSubscription!: Subscription;

  constructor() { }

  ngOnInit(): void {
    this.initializeSelectFields();
    this.formBuilder();
  }

  ngOnDestroy(): void {
    if (this.changesProductValueSubscription)
      this.changesProductValueSubscription.unsubscribe();

    if (this.valueChangesSubscription)
      this.valueChangesSubscription.unsubscribe();

    this.formValueEvent.emit(this.productForm.getRawValue());
  }

  initializeSelectFields(): void {
    const banks: IBankCommission[] = this.parameters.Banks;
    const products: IProductCommission[] = this.products;
    const companies: ICompanyCommission[] = this.parameters.Enterprises;

    this.generateBankSelectFields(banks);
    this.generateProductSelectFields(products);
    this.generateCompanySelectFields(companies);
  }

  generateBankSelectFields(banks: IBankCommission[]): void {
    this.bankSelectFields = banks.map((bank) => {
      return { id: bank.BankId, name: bank.Name };
    });
  }

  generateProductSelectFields(products: IProductCommission[]): void {
    this.productSelectFields = products.map((product) => {
      return { id: product.ProductId, name: product.Name };
    });
  }

  generateCompanySelectFields(companies: ICompanyCommission[]): void {
    this.companySelectFields = companies.map((company) => {
      return { id: company.CompanyId, name: company.Name };
    });
  }

  formBuilder(): void {
    this.productForm = new FormGroup<IProductForm>({
      ProductComissionId: new FormControl<string>('', { nonNullable: true }),
      ComissionId: new FormControl<string>('', { nonNullable: true }),
      BankId: new FormControl<string>(
        {
          value: '',
          disabled: this.type === ActionEnum.Edit || !this.firstProduct
        },
        { nonNullable: true }
      ),
      ProductId: new FormControl<string>('', { nonNullable: true }),
      CompanyId: new FormControl<string>(
        {
          value: '',
          disabled: this.type === ActionEnum.Edit || !this.firstProduct
        },
        { nonNullable: true }
      ),
      Period: new FormControl<string>(
        { value: '', disabled: !this.firstProduct },
        { nonNullable: true }
      ),
      CommissionType: new FormControl<number>(
        { value: 2, disabled: false },
        { nonNullable: true }
      ),
      MaxCommissionPerContract: new FormControl<number | null>(
        { value: null, disabled: false },
        { nonNullable: false }
      ),
      Productions: new FormArray<FormGroup<IProductionForm>>(
        this.formArrayBuilderEdit()
      )
    });

    this.productForm.patchValue(this.product);
    this.listeningChangesProductValue();
    this.listeningChangesCommissionTypeValue();
  }

  listeningChangesProductValue(): void {
    this.changesProductValueSubscription = this.productForm.controls[
      'ProductId'
    ].valueChanges.subscribe({
      next: (response) => {
        if (this.firstProduct && this.type === ActionEnum.Edit) {
          this.setValuesNewProduct();
        }

        this.changesProductIdValueEvent.emit(response);
      }
    });
  }

  listeningChangesCommissionTypeValue(): void {
    this.changesProductValueSubscription = this.productForm.controls[
      'CommissionType'
    ].valueChanges.subscribe({
      next: (response) => {
        this.setValuesProduction();
      }
    });

    this.productForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((response) => {
        this.insertProductToCommission();
        this.product.Productions = this.productForm.getRawValue()
          .Productions as IProduction[];
        this.convertStringToNumberProductions();
      });
  }

  setValuesNewProduct(): void {
    this.productForm.controls['ProductComissionId'].setValue("");
    this.product.ProductComissionId = "";
    this.setValuesProduction();
  }

  setValuesProduction(): void {
    this.formGroupsProductions.clear();
    this.insertProduction();
  }

  insertProductToCommission(): void {
    const product: IProduct = this.productForm.getRawValue();
    let index: number = this.commission.ProductsComission.findIndex(
      (prod) => prod.ProductId === product.ProductId
    );

    if (index >= 0) {
      this.commission.ProductsComission[index].BankId = product.BankId;
      this.commission.ProductsComission[index].CompanyId = product.CompanyId;
      this.commission.ProductsComission[index].Period = product.Period;
      this.commission.ProductsComission[index].ComissionId = product.ComissionId;
      this.commission.ProductsComission[index].CommissionType = product.CommissionType;
      this.commission.ProductsComission[index].MaxCommissionPerContract = product.MaxCommissionPerContract;

      if (this.firstProduct)
        this.changesFormValueEvent.emit({
          BankId: product.BankId,
          Period: product.Period,
          CompanyId: product.CompanyId,
          CommissionType: product.CommissionType,
          MaxCommissionPerContract: product.MaxCommissionPerContract
        });
    }
  }

  convertStringToNumberProductions(): void {
    this.product.Productions.forEach((production) => {
      production.InitialProduction = Number(production.InitialProduction);
      production.FinalProduction = Number(production.FinalProduction);
      production.InvoiceValue = Number(production.InvoiceValue);
      production.Percentage = Number(production.Percentage);
      production.TransferValue = Number(production.TransferValue);
    });
  }

  formArrayBuilderEdit(): FormGroup<IProductionForm>[] {
    const productions: FormGroup<IProductionForm>[] = [];

    this.product.Productions.forEach((production) => {
      const productionGroup: { [key: string]: FormControl<string | number> } =
        {};

      Object.entries(production).forEach(([key, value]) => {
        productionGroup[key] = new FormControl<string | number>(value, {
          nonNullable: true
        });
      });

      productions.push(
        new FormGroup<IProductionForm>(
          productionGroup as unknown as IProductionForm
        )
      );
    });

    return productions;
  }

  get formGroupsProductions() {
    return this.productForm.controls['Productions'] as FormArray;
  }

  isCommissionType(type: number): boolean {
    const product = this.productForm.getRawValue() as IProduct;
    return product.CommissionType === type;
  }

  insertProduction(): void {
    const productionGroup: FormGroup<IProductionForm> =
      new FormGroup<IProductionForm>({
        ProductionID: new FormControl<string>('', { nonNullable: true }),
        InitialProduction: new FormControl<number>(0, { nonNullable: true }),
        FinalProduction: new FormControl<number>(0, { nonNullable: true }),
        Percentage: new FormControl<number>(0, { nonNullable: true }),
        InvoiceValue: new FormControl<number>(0, { nonNullable: true }),
        TransferValue: new FormControl<number>(0, { nonNullable: true })
      });

    this.formGroupsProductions.push(productionGroup);
  }

  removeProduction(index: number): void {
    this.formGroupsProductions.removeAt(index);
  }
}
